import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  attachSupportTicketOrder,
  claimSupportTicket,
  queryKeys,
  releaseSupportTicket,
  replySupportTicket,
  resolveSupportTicket,
  SupportTicket,
  useBusyAction,
} from '@vgrubs/components';

/**
 * Working a ticket: claim it, hand it back, write to the merchant, close it,
 * or say which order it is about.
 *
 * Every route answers with the whole ticket, so each write is applied straight
 * to the detail cache - and the lists are only invalidated, because a ticket
 * changing status usually moves it to a different tab entirely.
 */
/**
 * Where a ticket the server just answered with goes: into its own detail, and
 * then the lists are refetched, because a ticket that changed status usually
 * belongs in a different tab than the one showing it.
 */
const applyTicket = (
  queryClient: ReturnType<typeof useQueryClient>,
  ticket: SupportTicket
) => {
  queryClient.setQueryData(queryKeys.supportTickets.detail(ticket.id), ticket);
  queryClient.invalidateQueries({ queryKey: queryKeys.supportTickets.listAll });
  queryClient.invalidateQueries({
    queryKey: queryKeys.supportTickets.openCount,
  });
};

/**
 * Claiming straight from a row in the list, without opening it first.
 *
 * Separate from the modal's hook because the id is per click here, not per
 * mounted component - and the row needs to know which ticket is mid-claim so
 * only that one shows it.
 */
export const useClaimTicket = () => {
  const queryClient = useQueryClient();
  const [claimingId, setClaimingId] = useState<number | null>(null);
  const { busy, run } = useBusyAction();

  const claim = async (ticketId: number) => {
    if (busy) return;
    setClaimingId(ticketId);
    await run(async () => {
      const { data } = await claimSupportTicket(ticketId);
      applyTicket(queryClient, data.data);
    }, 'Ticket claimed.');
    setClaimingId(null);
  };

  return { claim, claimingId };
};

export const useSupportTicketActions = (ticketId: number | null) => {
  const queryClient = useQueryClient();

  const apply = (ticket: SupportTicket) => applyTicket(queryClient, ticket);

  /* The refresh a FAILED write needs: every 400 here is somebody else having
     got there first ("Already claimed by Ana."), so the honest next step is to
     show what is true now rather than leave a button offering it again. */
  const refresh = () => {
    if (ticketId === null) return;
    queryClient.invalidateQueries({
      queryKey: queryKeys.supportTickets.detail(ticketId),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.supportTickets.listAll,
    });
  };

  const { busy, run } = useBusyAction();

  const call = async (
    action: () => Promise<{ data: { data: SupportTicket } }>,
    message: string
  ) => {
    const ok = await run(async () => {
      const { data } = await action();
      apply(data.data);
    }, message);
    if (!ok) refresh();
    return ok;
  };

  return {
    busy,
    claim: () =>
      call(() => claimSupportTicket(ticketId as number), 'Ticket claimed.'),
    release: () =>
      call(
        () => releaseSupportTicket(ticketId as number),
        'Ticket handed back.'
      ),
    /* An open ticket comes back claimed by the reader, and applying the
       answer is all it takes for the modal to show that. */
    reply: (message: string) =>
      call(
        () => replySupportTicket(ticketId as number, { message }),
        'Reply sent.'
      ),
    resolve: (resolution: string) =>
      call(
        () => resolveSupportTicket(ticketId as number, { resolution }),
        'Resolved, reply sent to the merchant.'
      ),
    attachOrder: (orderId: number) =>
      call(
        () => attachSupportTicketOrder(ticketId as number, orderId),
        'Order attached.'
      ),
  };
};
